import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../components/layout/Header';
import {
  createCampaign, deleteCampaign, getCampaign, getCampaignOptions, getLineProtection,
  launchCampaign, previewAudience, previewMessages, updateCampaign,
} from '../services/apiCampaigns';

// Puntos de partida. Las etapas las elige cada negocio: sus nombres cambian.
const PLANTILLAS_DE_INICIO = [
  {
    clave: 'seguimiento',
    titulo: 'Seguimiento de cotizaciones',
    detalle: 'Automática: a quien cotizó y no respondió en 2 días.',
    icono: 'schedule_send',
    datos: {
      nombre: 'Seguimiento de cotizaciones', recurrente: true, modo: 'ia',
      intencion: 'Retomar la cotización que le enviamos: preguntarle si le quedó alguna duda o si quiere avanzar con la compra.',
      filtros: { dias_sin_actividad_min: 2, dias_sin_actividad_max: 7 },
    },
  },
  {
    clave: 'frios',
    titulo: 'Reactivar leads fríos',
    detalle: 'Quienes consultaron hace 1 a 4 meses y no compraron.',
    icono: 'ac_unit',
    datos: {
      nombre: 'Reactivar leads fríos', recurrente: false, modo: 'ia',
      intencion: 'Saludar, recordarle con naturalidad lo que había consultado y decirle que seguimos a la orden si quiere retomarlo.',
      filtros: { dias_sin_actividad_min: 30, dias_sin_actividad_max: 120 },
    },
  },
  {
    clave: 'oferta',
    titulo: 'Oferta puntual',
    detalle: 'Una promoción con fecha, a quienes ya consultaron.',
    icono: 'local_offer',
    datos: {
      nombre: 'Oferta de la semana', recurrente: false, modo: 'ia', intencion: '',
      filtros: { dias_sin_actividad_min: 3, dias_sin_actividad_max: 180 },
    },
  },
];

const VACIA = {
  nombre: '', recurrente: false, modo: 'ia', intencion: '', plantilla: '', incluir_baja: true,
  whatsapp_account_id: null, inicia_at: null, filtros: {},
};

const Seccion = ({ numero, titulo, children, ayuda }) => (
  <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6 space-y-5">
    <div className="flex items-start gap-3">
      <span className="size-7 shrink-0 rounded-full bg-primary/10 text-primary text-sm font-black flex items-center justify-center">{numero}</span>
      <div>
        <h3 className="font-bold text-slate-900 dark:text-white">{titulo}</h3>
        {ayuda && <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{ayuda}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Etiqueta = ({ children }) => (
  <span className="block text-[11px] font-black text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">{children}</span>
);

const campoClase = 'w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40';

const Chips = ({ opciones, seleccion = [], onChange, vacio }) => {
  if (!opciones.length) return <p className="text-xs text-slate-400">{vacio}</p>;
  const alternar = (v) => onChange(seleccion.includes(v) ? seleccion.filter(x => x !== v) : [...seleccion, v]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {opciones.map(o => {
        const activo = seleccion.includes(o.valor);
        return (
          <button
            key={o.valor}
            type="button"
            onClick={() => alternar(o.valor)}
            aria-pressed={activo}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${activo
              ? 'bg-primary text-white border-primary'
              : 'bg-white dark:bg-white/5 text-slate-600 dark:text-gray-300 border-slate-200 dark:border-white/10 hover:border-primary/40'}`}
          >
            {o.etiqueta}
          </button>
        );
      })}
    </div>
  );
};

// El campo datetime-local habla en hora del navegador; el servidor guarda en UTC.
const aCampoLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const desdeCampoLocal = (valor) => (valor ? new Date(valor).toISOString() : null);

const numeroONulo = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const esNueva = !id;

  const [c, setC] = useState(VACIA);
  const [cargada, setCargada] = useState(esNueva);
  const [audiencia, setAudiencia] = useState(null);
  const [muestras, setMuestras] = useState(null);
  const [cargandoMuestras, setCargandoMuestras] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [confirmar, setConfirmar] = useState(false);

  const { data: opciones } = useQuery({ queryKey: ['campaign-options'], queryFn: async () => (await getCampaignOptions()).data });
  const { data: proteccion } = useQuery({ queryKey: ['campaigns-protection'], queryFn: async () => (await getLineProtection()).data });

  useEffect(() => {
    if (esNueva) return;
    getCampaign(id).then(({ data }) => {
      if (data.estado !== 'borrador') {
        navigate(`/campaigns/${id}`, { replace: true });
        return;
      }
      setC({ ...VACIA, ...data, filtros: data.filtros ?? {} });
      setCargada(true);
    });
  }, [id, esNueva, navigate]);

  // Línea por defecto: la primera activa.
  useEffect(() => {
    if (!c.whatsapp_account_id && opciones?.lineas?.length) {
      const activa = opciones.lineas.find(l => l.activa) ?? opciones.lineas[0];
      setC(prev => ({ ...prev, whatsapp_account_id: activa.id }));
    }
  }, [opciones, c.whatsapp_account_id]);

  // Audiencia en vivo, con una pausa para no consultar en cada tecla.
  const filtrosClave = JSON.stringify(c.filtros);
  useEffect(() => {
    if (!cargada) return undefined;
    const t = setTimeout(() => {
      previewAudience(c.filtros).then(({ data }) => setAudiencia(data)).catch(() => setAudiencia(null));
    }, 500);
    return () => clearTimeout(t);
  }, [filtrosClave, cargada]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiar = (campo, valor) => setC(prev => ({ ...prev, [campo]: valor }));
  const cambiarFiltro = (campo, valor) => setC(prev => ({ ...prev, filtros: { ...prev.filtros, [campo]: valor } }));

  const etapas = useMemo(() => (opciones?.etapas ?? []).map(e => ({
    valor: e.slug, etiqueta: e.name + (e.is_won ? ' (ganado)' : e.is_lost ? ' (perdido)' : ''),
  })), [opciones]);

  const payload = () => ({
    ...c,
    filtros: Object.fromEntries(Object.entries(c.filtros).filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && !v.length))),
  });

  const guardar = async () => {
    setGuardando(true);
    setErrores({});
    try {
      const { data } = esNueva ? await createCampaign(payload()) : await updateCampaign(id, payload());
      queryClient.invalidateQueries(['campaigns']);
      return data;
    } catch (err) {
      setErrores(err.response?.data?.errors ?? { general: [err.response?.data?.message ?? 'No se pudo guardar.'] });
      return null;
    } finally {
      setGuardando(false);
    }
  };

  const guardarYSalir = async () => {
    const data = await guardar();
    if (data) navigate('/campaigns');
  };

  const lanzar = async () => {
    const data = await guardar();
    if (!data) { setConfirmar(false); return; }
    try {
      await launchCampaign(data.id);
      queryClient.invalidateQueries(['campaigns']);
      navigate(`/campaigns/${data.id}`);
    } catch (err) {
      setConfirmar(false);
      setErrores(err.response?.data?.errors ?? { general: [err.response?.data?.message ?? 'No se pudo lanzar.'] });
      if (esNueva) navigate(`/campaigns/${data.id}/edit`, { replace: true });
    }
  };

  const verEjemplos = async () => {
    setCargandoMuestras(true);
    setMuestras(null);
    try {
      const { data } = await previewMessages(payload());
      setMuestras(data.muestras);
    } catch (err) {
      setErrores(err.response?.data?.errors ?? {});
    } finally {
      setCargandoMuestras(false);
    }
  };

  const borrar = async () => {
    if (!window.confirm('¿Borrar este borrador?')) return;
    await deleteCampaign(id);
    queryClient.invalidateQueries(['campaigns']);
    navigate('/campaigns');
  };

  const insertarVariable = (v) => cambiar('plantilla', `${c.plantilla ?? ''}{${v}}`);

  const linea = proteccion?.lineas?.find(l => l.linea_id === c.whatsapp_account_id) ?? proteccion?.lineas?.[0];
  const elegibles = audiencia?.elegibles ?? 0;
  const diasEstimados = linea ? Math.max(1, Math.ceil(elegibles / Math.max(1, Math.min(linea.tope_dia_maximo, linea.tope_dia_hoy)))) : null;
  const error = (campo) => errores[campo]?.[0];

  if (!cargada) return <div className="p-8 text-text-secondary">Cargando…</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
      <Header title={esNueva ? 'Nueva campaña' : 'Editar campaña'} />

      <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="space-y-6">
            <button onClick={() => navigate('/campaigns')} className="text-sm font-bold text-slate-500 hover:text-primary flex items-center gap-1">
              <span className="material-symbols-outlined text-[18px]">arrow_back</span> Campañas
            </button>

            {esNueva && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PLANTILLAS_DE_INICIO.map(p => (
                  <button
                    key={p.clave}
                    type="button"
                    onClick={() => setC(prev => ({ ...prev, ...p.datos, filtros: { ...p.datos.filtros, etapas: prev.filtros.etapas } }))}
                    className="text-left bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-4 hover:border-primary/40 transition-colors"
                  >
                    <span className="material-symbols-outlined text-primary">{p.icono}</span>
                    <p className="text-sm font-bold text-slate-900 dark:text-white mt-1">{p.titulo}</p>
                    <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-0.5">{p.detalle}</p>
                  </button>
                ))}
              </div>
            )}

            {error('general') && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-500/10 rounded-xl px-4 py-3">{error('general')}</p>}

            <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6">
              <Etiqueta>Nombre de la campaña</Etiqueta>
              <input className={campoClase} value={c.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Ej.: Porcelanato 60x60 en oferta" maxLength={150} />
              {error('nombre') && <p className="text-xs text-red-600 mt-1">{error('nombre')}</p>}
            </div>

            <Seccion numero="1" titulo="¿A quién?" ayuda="Las reglas de seguridad se aplican siempre: sólo contactos que escribieron alguna vez, nunca en medio de una conversación, nunca a quien pidió la baja.">
              <div>
                <Etiqueta>Etapas</Etiqueta>
                <Chips opciones={etapas} seleccion={c.filtros.etapas ?? []} onChange={v => cambiarFiltro('etapas', v)} vacio="Sin etapas configuradas" />
                <p className="text-[11px] text-slate-400 mt-1.5">Sin ninguna marcada, entran todas.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label>
                  <Etiqueta>Sin actividad desde (días)</Etiqueta>
                  <input type="number" min="0" max="730" className={campoClase} value={c.filtros.dias_sin_actividad_min ?? ''}
                    onChange={e => cambiarFiltro('dias_sin_actividad_min', numeroONulo(e.target.value))} placeholder="Ej.: 7" />
                </label>
                <label>
                  <Etiqueta>Hasta (días)</Etiqueta>
                  <input type="number" min="1" max="730" className={campoClase} value={c.filtros.dias_sin_actividad_max ?? ''}
                    onChange={e => cambiarFiltro('dias_sin_actividad_max', numeroONulo(e.target.value))} placeholder="Ej.: 90" />
                  {error('filtros.dias_sin_actividad_max') && <p className="text-xs text-red-600 mt-1">Tiene que ser mayor que el mínimo.</p>}
                </label>
              </div>
              <div>
                <Etiqueta>Vendedor asignado</Etiqueta>
                <Chips opciones={(opciones?.vendedores ?? []).map(v => ({ valor: v.id, etiqueta: v.name }))}
                  seleccion={c.filtros.vendedores ?? []} onChange={v => cambiarFiltro('vendedores', v)} vacio="Sin vendedores" />
              </div>
              {opciones?.etiquetas?.length > 0 && (
                <div>
                  <Etiqueta>Etiquetas</Etiqueta>
                  <Chips opciones={opciones.etiquetas.map(t => ({ valor: t, etiqueta: t }))}
                    seleccion={c.filtros.etiquetas ?? []} onChange={v => cambiarFiltro('etiquetas', v)} />
                </div>
              )}
              <label className="block">
                <Etiqueta>Interés (palabra en lo que consultó)</Etiqueta>
                <input className={campoClase} value={c.filtros.interes ?? ''} onChange={e => cambiarFiltro('interes', e.target.value)} placeholder="Ej.: porcelanato" maxLength={100} />
              </label>
            </Seccion>

            <Seccion numero="2" titulo="¿Qué le decimos?">
              <div className="grid grid-cols-2 gap-2" role="radiogroup">
                {[
                  { valor: 'ia', titulo: 'Redactado por la IA', detalle: 'Un mensaje distinto para cada persona, según lo que habló. Recomendado.', icono: 'auto_awesome' },
                  { valor: 'plantilla', titulo: 'Plantilla', detalle: 'Un texto con variables. Más control, más riesgo de parecer difusión.', icono: 'description' },
                ].map(o => (
                  <button key={o.valor} type="button" role="radio" aria-checked={c.modo === o.valor} onClick={() => cambiar('modo', o.valor)}
                    className={`text-left p-3.5 rounded-xl border transition-colors ${c.modo === o.valor ? 'border-primary/50 bg-primary/[0.06]' : 'border-slate-200 dark:border-white/10 hover:border-primary/30'}`}>
                    <p className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[18px] text-primary">{o.icono}</span>{o.titulo}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-1">{o.detalle}</p>
                  </button>
                ))}
              </div>

              {c.modo === 'ia' ? (
                <label className="block">
                  <Etiqueta>¿Qué quieres comunicar?</Etiqueta>
                  <textarea rows={4} className={campoClase} value={c.intencion ?? ''} onChange={e => cambiar('intencion', e.target.value)} maxLength={1000}
                    placeholder="Ej.: Esta semana el porcelanato 60x60 tiene 10% de descuento pagando en divisas, hasta el sábado. Invitarlo a pasar por la tienda o a pedir su cotización." />
                  <p className="text-[11px] text-slate-400 mt-1">Incluye los datos concretos (precio, plazo, condiciones): la IA no inventa nada que no esté acá.</p>
                  {error('intencion') && <p className="text-xs text-red-600 mt-1">{error('intencion')}</p>}
                </label>
              ) : (
                <div>
                  <Etiqueta>Texto</Etiqueta>
                  <textarea rows={4} className={campoClase} value={c.plantilla ?? ''} onChange={e => cambiar('plantilla', e.target.value)} maxLength={1000}
                    placeholder="{Hola|Buenas|Qué tal} {nombre}, ¿sigues interesado en {interes}? Esta semana…" />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(opciones?.variables ?? []).map(v => (
                      <button key={v} type="button" onClick={() => insertarVariable(v)}
                        className="text-[11px] font-bold px-2 py-1 rounded-lg bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary">
                        {`{${v}}`}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2 mt-2">
                    La línea no deja salir el mismo texto a más de 8 contactos por hora. Usa variantes como <code>{'{Hola|Buenas}'}</code> para que cada mensaje sea distinto, o elige el modo IA.
                  </p>
                  {error('plantilla') && <p className="text-xs text-red-600 mt-1">{error('plantilla')}</p>}
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 accent-primary" checked={!!c.incluir_baja} onChange={e => cambiar('incluir_baja', e.target.checked)} />
                <span>
                  <span className="text-sm font-bold text-slate-800 dark:text-white">Agregar la opción de baja al final</span>
                  <span className="block text-[11px] text-slate-500 dark:text-gray-400">
                    «{proteccion?.config?.pie_baja}». Quien no quiere más mensajes responde BAJA en vez de denunciar el número: es lo que más protege la línea.
                  </span>
                </span>
              </label>

              <button type="button" onClick={verEjemplos} disabled={cargandoMuestras || elegibles === 0}
                className="w-full py-2.5 rounded-xl border border-primary/30 text-primary text-sm font-bold hover:bg-primary/5 disabled:opacity-50 flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[18px]">visibility</span>
                {cargandoMuestras ? 'Redactando ejemplos…' : 'Ver ejemplos con contactos reales'}
              </button>

              {muestras && (
                <div className="space-y-3">
                  {muestras.length === 0 && <p className="text-xs text-slate-400">No hay contactos en la audiencia para mostrar ejemplos.</p>}
                  {muestras.map(m => (
                    <div key={m.contact_id}>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-gray-400 mb-1">Para {m.nombre}</p>
                      {m.mensaje ? (
                        <div className="bg-[#e7ffdb] dark:bg-emerald-900/30 text-slate-800 dark:text-gray-100 text-sm rounded-2xl rounded-tr-md px-4 py-3 whitespace-pre-wrap max-w-md ml-auto">{m.mensaje}</div>
                      ) : (
                        <p className="text-xs text-red-600">{m.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Seccion>

            <Seccion numero="3" titulo="¿Cómo sale?">
              <label className="block">
                <Etiqueta>Línea de WhatsApp</Etiqueta>
                <select className={campoClase} value={c.whatsapp_account_id ?? ''} onChange={e => cambiar('whatsapp_account_id', numeroONulo(e.target.value))}>
                  {(opciones?.lineas ?? []).map(l => <option key={l.id} value={l.id}>{l.nombre}{l.activa ? '' : ' (desactivada)'}</option>)}
                </select>
              </label>
              <label className="block">
                <Etiqueta>Empieza</Etiqueta>
                <input type="datetime-local" className={campoClase} value={aCampoLocal(c.inicia_at)}
                  onChange={e => cambiar('inicia_at', desdeCampoLocal(e.target.value))} />
                <p className="text-[11px] text-slate-400 mt-1">Vacío: apenas la lances (dentro del horario de envío).</p>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 accent-primary" checked={!!c.recurrente} onChange={e => cambiar('recurrente', e.target.checked)} />
                <span>
                  <span className="text-sm font-bold text-slate-800 dark:text-white">Automática</span>
                  <span className="block text-[11px] text-slate-500 dark:text-gray-400">
                    Mientras esté activa, suma sola a los contactos que empiezan a cumplir los filtros (cada 30 minutos). A cada uno le escribe una sola vez.
                  </span>
                </span>
              </label>
            </Seccion>
          </div>

          {/* Panel lateral: audiencia y acciones */}
          <aside className="lg:sticky lg:top-4 space-y-4">
            <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-5">
              <p className="text-[11px] font-black text-slate-500 dark:text-gray-400 uppercase tracking-wider">Audiencia</p>
              <p className="text-4xl font-black text-slate-900 dark:text-white tabular-nums mt-1">{audiencia?.elegibles ?? '—'}</p>
              <p className="text-xs text-slate-500 dark:text-gray-400">contactos recibirían esta campaña</p>
              {audiencia && audiencia.coinciden > audiencia.elegibles && (
                <div className="mt-4 space-y-1.5">
                  <p className="text-[11px] font-bold text-slate-500">De {audiencia.coinciden} que cumplen los filtros, se excluyen:</p>
                  {audiencia.exclusiones.map(x => (
                    <div key={x.regla} className="flex justify-between gap-2 text-[11px]">
                      <span className="text-slate-600 dark:text-gray-300">{x.descripcion}</span>
                      <span className="font-bold text-slate-900 dark:text-white tabular-nums">{x.excluidos}</span>
                    </div>
                  ))}
                </div>
              )}
              {audiencia?.muestra?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5 space-y-1">
                  {audiencia.muestra.map(m => (
                    <p key={m.id} className="text-xs text-slate-600 dark:text-gray-300 truncate">
                      {m.nombre}{m.interes ? <span className="text-slate-400"> · {m.interes}</span> : null}
                    </p>
                  ))}
                </div>
              )}
              {linea && elegibles > 0 && !c.recurrente && (
                <p className="mt-4 text-[11px] text-slate-500 dark:text-gray-400 bg-slate-50 dark:bg-white/5 rounded-lg px-3 py-2">
                  Al ritmo seguro de la línea (hasta {linea.tope_dia_hoy} por día hoy), tardaría unos <b>{diasEstimados} {diasEstimados === 1 ? 'día' : 'días'}</b>.
                </p>
              )}
              {error('audiencia') && <p className="text-xs text-red-600 mt-2">{error('audiencia')}</p>}
            </div>

            <div className="space-y-2">
              <button onClick={() => setConfirmar(true)} disabled={guardando || (!c.recurrente && elegibles === 0)}
                className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm">
                <span className="material-symbols-outlined text-[20px]">send</span> Lanzar campaña
              </button>
              <button onClick={guardarYSalir} disabled={guardando}
                className="w-full py-2.5 rounded-xl font-bold text-sm border border-slate-200 dark:border-white/10 text-slate-700 dark:text-gray-200 hover:bg-white dark:hover:bg-white/5">
                Guardar borrador
              </button>
              {!esNueva && (
                <button onClick={borrar} className="w-full py-2 text-xs font-bold text-red-500 hover:underline">Borrar borrador</button>
              )}
            </div>
          </aside>
        </div>
      </div>

      {confirmar && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-surface-dark rounded-2xl p-6 max-w-md w-full space-y-4 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">¿Lanzar «{c.nombre || 'campaña'}»?</h3>
            <ul className="text-sm text-slate-600 dark:text-gray-300 space-y-1.5">
              <li>• {c.recurrente ? 'Automática: irá sumando contactos mientras esté activa.' : `${elegibles} contactos.`}</li>
              <li>• Sale de a un mensaje, con pausas de {proteccion?.config?.pausa_min_seg}–{proteccion?.config?.pausa_max_seg} segundos, sólo en el horario de envío.</li>
              <li>• Se pausa sola si los mensajes fallan o no llegan.</li>
              <li>• Puedes pausarla o cancelarla en cualquier momento.</li>
            </ul>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmar(false)} className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-white/5">Volver</button>
              <button onClick={lanzar} disabled={guardando} className="px-4 py-2 rounded-xl text-sm font-bold bg-primary text-white disabled:opacity-50">
                {guardando ? 'Lanzando…' : 'Lanzar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
